import { Log, delay } from '@artup/utilities'
import { initializeAPI, CommonAPI } from '@artup/api-client'
import { Notification } from '../../services'

export default {
  /**
   * Initialize the application
   * @param {ApplicationOptions} options Application initialization options
   * @param {Object} pkg Application package data
   * @param {BootstrapConfiguration} bootstrap Bootstrap configuration
   */
  async initialize (options, pkg, bootstrap) {
    if (!pkg) throw new Error('Application package data is required')
    const { name, version, description } = pkg

    Log.info(`[${name}] Loading ...`)

    this.isInitialized = false
    this.initializationFailed = false
    this.options = options
    this.application.name = name
    this.application.description = description
    this.application.version = version
    this.application.url = window.location.toString()

    // Start the ticker
    window.setInterval(() => {
      this.tick()
    }, 1000)

    // Set up global click event, useful for dismissing popups etc.
    document.addEventListener('click', event => {
      this.click({ event })
    })

    // If bootstrap configuration is specified, retrieve application configuration
    if (bootstrap) {
      Log.initialize(bootstrap.logLevel)
      const { client, key, api: url } = bootstrap
      await CommonAPI.initialize({ url, client, key })
      const { configuration, environment, flags } = await CommonAPI.getClientConfiguration({ client, key }) || {}
      if (configuration) {
        Log.info(`[${name}]`, configuration.environment.toUpperCase())
        Log.info(`[${name}] Configuration`, configuration)
        this.configuration = configuration
        this.environment = environment
        this.flags = flags

        // Initialize API clients
        await initializeAPI(configuration)
      }
    }
  },

  /**
   * Initializes an applet into the application
   * @param {AppletOptions} applet Applet options
   */
  async initializeApplet (applet) {
    if (!applet) throw new Error('Applet is required')

    // Call applet's store initialization method
    await applet.store.initialize()

    Log.info(`[${applet.name}] Applet initialized`)
    this.applets[applet.id] = applet
  },

  /**
   * Signal completed initialization of the application
   */
  async initialized () {
    this.isInitialized = true
    this.initializationFailed = false
    Log.info(`[${this.application.name}] Ready`, this.$state)
  },

  /**
   * Signal failed initialization of the application
   */
  async initializationFailed () {
    this.isInitialized = false
    this.initializationFailed = true
    Log.info(`[${this.application.name}]Initialization failed`)
  },

  /**
   * Waits for a while
   * @param {Number} interval Wait interval in milliseconds
   */
  async wait ({ interval = 1000 } = {}) {
    await delay(interval)
  },

  /**
   * Report an unhandled error
   * @param {Error} error Exception
   * @param {String} message User-friendly message to show
   */
  async fail ({ error, message }) {
    // Cancel any notifications of pending operations
    await this.done()
    // Store the error
    this.error = {
      exception: error,
      message
    }
  },

  /**
   * Recovers after an unhandled error
  */
  async recover () {
    // Cancel any notifications of pending operations
    await this.done()
    // Cancel the error
    this.error = {}
  },

  /**
   * Indicates start of a long-lasting activity,
   * displays progress notification if message is specified
   * @param {String} action Action with which we're busy, unique code
   * @param {String} message User-friendly message describing the activity
   * @param {String} details More detailed description of the activity
   * @param {Object} data Data associated with the activity
   * @param {Boolean} silent If true, no notification is shown.
   * @param {Number} timeout If greater than zero, notification will disappear automatically, otherwise
   * it will stay until `done` is called
   * Can help reduce code complexity and unnecessary `if` statements in certain cases.
   */
  async busy ({ action, message, details, data, silent, timeout = 0 } = {}) {
    if (silent) () => { }

    // Close any pending busy notifications
    if (this.isBusy) {
      const { hideNotification } = this.activity
      if (hideNotification) {
        hideNotification()
      }
      this.done()
    }
    // Show new busy notification
    const hideNotification = message ? Notification.progress({ message, details, timeout }) : () => { }

    this.activity.now = true
    this.activity.startedAt = new Date()
    this.activity.finishedAt = null
    this.activity.action = action
    this.activity.message = message
    this.activity.details = details
    this.activity.data = data
    this.activity.hideNotification = hideNotification

    return hideNotification
  },

  /**
   * Indicates the end of a running activity, started with `busy` action.
   * Displays success or failure notification if message or error is specified.
   * @param {String} message User-friendly message to report with end of the activity
   * @param {String} warning Warning to report with end of the activity
   * @param {String} error Error to report with end of the activity
   * @param {String} details More detailed description of the activity
   * @param {Object} data Data associated with end of the activity
   * @param {Boolean} silent If true, no notification is shown.
   * Can help reduce code complexity and unnecessary `if` statements in certain cases.
   */
  async done ({ message, warning, error, details, data, silent } = {}) {
    if (silent) return

    // Hide pending progress notification...
    const { startedAt, hideNotification } = this.activity
    const duration = startedAt ? (new Date()) - startedAt : 0
    if (hideNotification) {
      // ...but wait a bit if activity was quickly finished,
      // otherwise we get those nasty blinking notifications
      if (duration < 1000) {
        await delay(1000 - duration)
      }
      hideNotification()
    }

    // Display finish notification after a brief while,
    // to give time to hide the progress notification
    if (message || warning || error) {
      await delay(500)
      if (message) {
        Notification.success({ message, details })
      } else if (warning) {
        Notification.warning({ message: warning, details })
      } else if (error) {
        Notification.error({ message: error, details })
      }
    }

    this.activity.now = false
    this.activity.finishedAt = new Date()
    this.activity.duration = (this.activity.finishedAt - this.activity.startedAt)
    this.activity.message = message || this.activity.message
    this.activity.warning = warning
    this.activity.error = error
    this.activity.details = details || this.activity.details
    this.activity.data = data || this.activity.data
    this.activity.hideNotification = null
  },

  /**
   * Ticks the client ticker
   */
  tick () {
    this.ticker++
  },

  /**
   * Registers a click
   */
  click (event) {
    this.click.count++
    this.click.event = event
  },

  /**
   * Reset action which reverts the underlying store to its original this.
   * Implement in modules to make sure that they're cleared properly when requested,
   * for example when user logs out of the application
   */
  async reset () {
    this.$reset()
  }
}
